import { Collapse } from '@material-ui/core';
import cx from 'classnames';
import { FC, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Badge, BadgeProps, BadgeSize } from '@shared/components/badge-new';
import { Flex } from '@shared/components/flex';
import { Link } from '@shared/components/link';
import { useAppUrl } from '@shared/contexts/ConfigContext';
import { ChevronDownIcon } from '@shared/icons/chevron-down';
import { ZapIcon } from '@shared/icons/zap';
import { checkIsActive } from '@shared/utils/common';
import { getTransformedRoute } from '@shared/utils/route';

import { styles } from './SideNavigationGroup.styles';

export interface SideNavigationGroupItem {
  badge?: Omit<BadgeProps, 'classes'>;
  to: string;
  title: string;
}

export interface SideNavigationGroupProps extends WithStyles<typeof styles> {
  badge?: Omit<BadgeProps, 'classes'>;
  expanded?: boolean;
  fixed?: boolean;
  icon: ReactNode;
  title: string;
  items: SideNavigationGroupItem[];
  setExpanded?: UseStateSetter<boolean>;
}

const SideNavigationGroupComponent: FC<SideNavigationGroupProps> = ({
  badge,
  classes,
  expanded,
  fixed,
  icon,
  items,
  title,
  setExpanded,
}) => {
  const { appUrl } = useAppUrl();
  const { pathname } = useLocation();

  const activeHash = useMemo(
    () =>
      items.reduce((result, item) => {
        const transformedTo = getTransformedRoute(appUrl, item.to);
        result[item.to] = checkIsActive(pathname, transformedTo);

        return result;
      }, {} as Record<string, boolean>),
    [appUrl, items, pathname]
  );

  const renderedItems = useMemo(
    () =>
      items.map(({ to, title, badge }, index) => (
        <Link
          key={index}
          classes={{
            root: classes.link,
            rootInternal: cx({ [classes.linkActive]: activeHash[to] ?? false }),
          }}
          to={to}
        >
          <div className={classes.linkDotContainer}>
            <div className={classes.linkDot} />
          </div>
          <span className={classes.linkText}>{title}</span>
          {!!badge && (
            <Badge
              size={BadgeSize.small}
              startIcon={<ZapIcon classes={{ root: classes.badgeIcon }} />}
              {...badge}
              classes={{ badge: classes.linkBadge }}
            />
          )}
        </Link>
      )),
    [classes, items, pathname]
  );

  return (
    <div className={classes.root}>
      <Flex
        alignItems="center"
        className={cx(classes.toggleButton, { [classes.toggleButtonActive]: fixed })}
        wrap="nowrap"
        onClick={setExpanded ? () => setExpanded((_prev) => !_prev) : undefined}
      >
        <span className={classes.toggleStartIcon}>{icon}</span>
        <span className={classes.toggleText}>{title}</span>
        {!!badge && (
          <Badge
            size={BadgeSize.small}
            startIcon={<ZapIcon classes={{ root: classes.badgeIcon }} />}
            {...badge}
            classes={{ badge: classes.linkBadge }}
          />
        )}
        {!fixed && (
          <ChevronDownIcon classes={{ root: cx(classes.toggleIcon, { [classes.toggleIconCollapsed]: expanded }) }} />
        )}
      </Flex>
      <Collapse in={fixed ? true : expanded} timeout={400} unmountOnExit>
        <Flex direction="column">{renderedItems}</Flex>
      </Collapse>
    </div>
  );
};

export const SideNavigationGroup = withStyles(styles)(SideNavigationGroupComponent);
